/**
 * Issue Templates admin page (SmartImprove).
 *
 * Consulting-grade reusable warehouse transformation issue templates.
 * Backed by the existing `smartimprove_issue_library` collection (route
 * slug `/issue-library` preserved for backward compat) — user-facing name
 * is "Issue Templates". Technical fields (slug, impact_type, related_kpis,
 * recommendation_hints, roi_tags, implementation_complexity) are kept in
 * the DB for backward compat but HIDDEN from the admin UX so consultants
 * see a clean playbook, not a master-data form.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  issueLibraryApi,
  operationalAreasApi,
  subProcessesApi,
} from "../../lib/smartimproveAdminApi";

// Severity pill styles match the rest of SmartImprove admin.
const SEVERITY_PILL = {
  low: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
  medium: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  high: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]",
  critical: "bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]",
};

function slugify(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

const EMPTY = {
  id: null,
  slug: "",
  title: "",
  operational_area: "",
  sub_process_key: "",
  description: "",
  typical_symptoms: "",
  business_impact: "",
  kpi_impact: "",
  severity: "medium",
  industry_applicability: [],
  investigation_areas: "",
  active: true,
  sort_order: 0,
};

function IndustryChips({ values }) {
  if (!values?.length) return <span className="text-[#9CA3AF]">—</span>;
  const visible = values.slice(0, 3);
  return (
    <div className="flex flex-wrap gap-1">
      {visible.map((v) => (
        <span
          key={v}
          className="px-1.5 py-0.5 text-[10px] rounded-sm border border-[#E5E7EB] bg-[#F8FAFC] text-[#374151] capitalize"
        >
          {v.replace(/_/g, " ")}
        </span>
      ))}
      {values.length > visible.length ? (
        <span className="text-[10px] text-[#6B7280]">
          +{values.length - visible.length}
        </span>
      ) : null}
    </div>
  );
}

function TemplateDialog({
  open,
  onOpenChange,
  value,
  processes,
  subProcesses,
  onChange,
  onSave,
  saving,
}) {
  const isEdit = !!value?.id;
  const subOptions = useMemo(
    () =>
      (subProcesses || [])
        .filter(
          (s) =>
            s.active !== false && s.process_key === value?.operational_area
        )
        .sort(
          (a, b) =>
            (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
            String(a.label).localeCompare(String(b.label))
        ),
    [subProcesses, value?.operational_area]
  );
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="it-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Issue Template" : "New Issue Template"}
          </DialogTitle>
          <DialogDescription>
            Canonical warehouse transformation issue pattern. Used during
            assessments, workshops, and AI-assisted recommendation generation.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Issue Template Name
            </label>
            <Input
              value={value?.title || ""}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="e.g. Excessive picker travel time"
              className="rounded-sm text-xs"
              data-testid="it-field-title"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Business Process
            </label>
            <Select
              value={value?.operational_area || ""}
              onValueChange={(v) =>
                set({ operational_area: v, sub_process_key: "" })
              }
            >
              <SelectTrigger className="rounded-sm text-xs" data-testid="it-field-process">
                <SelectValue placeholder="Select business process" />
              </SelectTrigger>
              <SelectContent>
                {processes
                  .filter((p) => p.active !== false)
                  .map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Sub-Process
            </label>
            <Select
              value={value?.sub_process_key || ""}
              onValueChange={(v) => set({ sub_process_key: v })}
              disabled={!value?.operational_area}
            >
              <SelectTrigger className="rounded-sm text-xs" data-testid="it-field-sub-process">
                <SelectValue
                  placeholder={
                    value?.operational_area
                      ? "Select sub-process (optional)"
                      : "Select business process first"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {subOptions.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Description
            </label>
            <Textarea
              value={value?.description || ""}
              onChange={(e) => set({ description: e.target.value })}
              rows={2}
              className="rounded-sm text-xs"
              data-testid="it-field-description"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Typical Symptoms
            </label>
            <Textarea
              value={value?.typical_symptoms || ""}
              onChange={(e) => set({ typical_symptoms: e.target.value })}
              rows={2}
              className="rounded-sm text-xs"
              placeholder="What does this look like on the floor?"
              data-testid="it-field-symptoms"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Business Impact
            </label>
            <Textarea
              value={value?.business_impact || ""}
              onChange={(e) => set({ business_impact: e.target.value })}
              rows={2}
              className="rounded-sm text-xs"
              placeholder="What does the business lose if this is not fixed?"
              data-testid="it-field-business-impact"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Typical KPI Impact
            </label>
            <Textarea
              value={value?.kpi_impact || ""}
              onChange={(e) => set({ kpi_impact: e.target.value })}
              rows={2}
              className="rounded-sm text-xs"
              placeholder="Which KPIs typically move? (e.g. picking productivity, order cycle time)"
              data-testid="it-field-kpi-impact"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Severity
            </label>
            <Select
              value={value?.severity || "medium"}
              onValueChange={(v) => set({ severity: v })}
            >
              <SelectTrigger className="rounded-sm text-xs" data-testid="it-field-severity">
                <SelectValue placeholder="Select severity" />
              </SelectTrigger>
              <SelectContent>
                {["low", "medium", "high", "critical"].map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Industry Applicability
            </label>
            <Input
              value={(value?.industry_applicability || []).join(", ")}
              onChange={(e) =>
                set({
                  industry_applicability: e.target.value
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
              placeholder="all, pharma, food_beverage, retail …"
              className="rounded-sm text-xs"
              data-testid="it-field-industry"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Recommended Investigation Areas
            </label>
            <Textarea
              value={value?.investigation_areas || ""}
              onChange={(e) => set({ investigation_areas: e.target.value })}
              rows={2}
              className="rounded-sm text-xs"
              placeholder="Where should the consultant look first? (e.g. SOP, RF flow, master data)"
              data-testid="it-field-investigation"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="it-active"
              type="checkbox"
              checked={value?.active !== false}
              onChange={(e) => set({ active: e.target.checked })}
              data-testid="it-field-active"
            />
            <label htmlFor="it-active" className="text-xs text-[#374151]">
              Active
            </label>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Sort order
            </label>
            <Input
              type="number"
              value={value?.sort_order ?? 0}
              onChange={(e) => set({ sort_order: e.target.value })}
              className="rounded-sm text-xs"
              data-testid="it-field-sort-order"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-sm text-xs"
            data-testid="it-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={
              saving || !value?.title?.trim() || !value?.operational_area
            }
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="it-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function IssueTemplatesPage() {
  const [rows, setRows] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [subProcesses, setSubProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filterProcess, setFilterProcess] = useState("__all");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [items, bps, sps] = await Promise.all([
        issueLibraryApi.list(),
        operationalAreasApi.list(),
        subProcessesApi.list(),
      ]);
      setRows(items || []);
      setProcesses(bps || []);
      setSubProcesses(sps || []);
    } catch (e) {
      toast.error("Failed to load issue templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const processByKey = useMemo(() => {
    const m = {};
    processes.forEach((p) => (m[p.key] = p));
    return m;
  }, [processes]);
  const subByKey = useMemo(() => {
    const m = {};
    subProcesses.forEach((s) => (m[s.key] = s));
    return m;
  }, [subProcesses]);

  const sortedRows = useMemo(() => {
    const filtered =
      filterProcess === "__all"
        ? rows
        : rows.filter((r) => r.operational_area === filterProcess);
    return [...filtered].sort(
      (a, b) =>
        (processByKey[a.operational_area]?.sort_order ?? 999) -
          (processByKey[b.operational_area]?.sort_order ?? 999) ||
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        String(a.title).localeCompare(String(b.title))
    );
  }, [rows, filterProcess, processByKey]);

  const openCreate = () => {
    setForm({ ...EMPTY });
    setDialog(true);
  };
  const openEdit = (row) => {
    setForm({
      id: row.id,
      slug: row.slug || "",
      title: row.title || "",
      operational_area: row.operational_area || "",
      sub_process_key: row.sub_process_key || "",
      description: row.description || "",
      typical_symptoms: row.typical_symptoms || "",
      business_impact: row.business_impact || "",
      kpi_impact: row.kpi_impact || "",
      severity: row.severity || "medium",
      industry_applicability: Array.isArray(row.industry_applicability)
        ? row.industry_applicability
        : [],
      investigation_areas: row.investigation_areas || "",
      active: row.active !== false,
      sort_order: row.sort_order ?? 0,
    });
    setDialog(true);
  };
  const save = async () => {
    const isEdit = !!form.id;
    const title = (form.title || "").trim();
    if (!title || !form.operational_area) return;
    const slug = isEdit ? form.slug : slugify(title);
    const payload = {
      slug,
      title,
      operational_area: form.operational_area,
      sub_process_key: form.sub_process_key || "",
      description: (form.description || "").trim(),
      typical_symptoms: (form.typical_symptoms || "").trim(),
      business_impact: (form.business_impact || "").trim(),
      kpi_impact: (form.kpi_impact || "").trim(),
      severity: form.severity || "medium",
      industry_applicability: form.industry_applicability || [],
      investigation_areas: (form.investigation_areas || "").trim(),
      active: !!form.active,
      sort_order: Number(form.sort_order) || 0,
    };
    setSaving(true);
    try {
      if (isEdit) await issueLibraryApi.update(form.id, payload);
      else await issueLibraryApi.create(payload);
      toast.success(`Issue template ${isEdit ? "updated" : "created"}`);
      setDialog(false);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await issueLibraryApi.remove(row.id);
      toast.success("Issue template deleted");
      await reload();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-5" data-testid="si-issue-library">
      <header className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            SmartImprove · Configuration
          </p>
          <h1
            className="text-2xl font-bold text-[#111827] mt-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Issue Templates
          </h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
            Reusable warehouse transformation issue templates used during
            assessments, workshops, and AI-assisted recommendation generation.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
          data-testid="it-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Issue Template
        </Button>
      </header>

      <div className="flex items-center gap-3">
        <label className="text-xs font-medium text-[#374151]">
          Filter by Business Process
        </label>
        <Select value={filterProcess} onValueChange={setFilterProcess}>
          <SelectTrigger
            className="rounded-sm text-xs w-72"
            data-testid="it-filter-process"
          >
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All Business Processes</SelectItem>
            {processes
              .filter((p) => p.active !== false)
              .map((p) => (
                <SelectItem key={p.key} value={p.key}>
                  {p.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#6B7280] ml-auto">
          {sortedRows.length} template{sortedRows.length === 1 ? "" : "s"}
        </span>
      </div>

      <section className="bg-white border border-[#E5E7EB] rounded-sm">
        {loading ? (
          <div className="px-5 py-8 text-sm text-[#6B7280]">Loading…</div>
        ) : sortedRows.length === 0 ? (
          <div
            className="px-5 py-12 text-center text-sm text-[#6B7280]"
            data-testid="it-empty"
          >
            No issue templates yet. Click <strong>Add Issue Template</strong>{" "}
            to create the first one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="px-4 py-2 font-semibold">Issue Template</th>
                <th className="px-4 py-2 font-semibold">Business Process</th>
                <th className="px-4 py-2 font-semibold">Sub-Process</th>
                <th className="px-4 py-2 font-semibold">Typical Impact</th>
                <th className="px-4 py-2 font-semibold">Severity</th>
                <th className="px-4 py-2 font-semibold">Industry</th>
                <th className="px-4 py-2 font-semibold text-right">Status</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => {
                const bp = processByKey[row.operational_area];
                const sp = subByKey[row.sub_process_key];
                const sev = String(row.severity || "medium").toLowerCase();
                return (
                  <tr
                    key={row.id}
                    className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC] align-top"
                    data-testid={`it-row-${row.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-[#111827] font-medium">
                        {row.title}
                      </div>
                      {row.description ? (
                        <p className="text-[11px] text-[#6B7280] mt-0.5 line-clamp-2 max-w-md">
                          {row.description}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {bp?.label || (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {sp?.label || (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[11px] text-[#374151] max-w-xs">
                      {row.business_impact ? (
                        <span className="line-clamp-2">
                          {row.business_impact}
                        </span>
                      ) : row.impact_type ? (
                        <span className="capitalize text-[#6B7280]">
                          {String(row.impact_type).replace(/_/g, " ")}
                        </span>
                      ) : (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm capitalize ${
                          SEVERITY_PILL[sev] ||
                          "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]"
                        }`}
                      >
                        {sev}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <IndustryChips values={row.industry_applicability} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.active === false ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]">
                          inactive
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]">
                          active
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(row)}
                        className="text-xs font-semibold text-[#025979] hover:text-[#03738C] mr-3"
                        data-testid={`it-edit-${row.id}`}
                        aria-label={`Edit ${row.title}`}
                      >
                        <Pencil className="inline w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => remove(row)}
                        className="text-xs font-semibold text-[#B91C1C] hover:text-[#7F1D1D]"
                        data-testid={`it-delete-${row.id}`}
                        aria-label={`Delete ${row.title}`}
                      >
                        <Trash2 className="inline w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <TemplateDialog
        open={dialog}
        onOpenChange={setDialog}
        value={form}
        processes={processes}
        subProcesses={subProcesses}
        onChange={setForm}
        onSave={save}
        saving={saving}
      />
    </div>
  );
}
